export enum AuthorizationPolicy {
  PosAccess = "posAccess",
  Sale = "sale",
  Return = "return",
  FinancialDocuments = "financialDocuments",
  WithdrawWithDocument = "withdrawWithDocument",
  Cancellation = "cancellation",
}

export enum ReturnPaymentMethod {
  Cash = "cash",
  Card = "card",
  Benefits = "benefits",
  Other = "other",
  Voucher = "voucher",
}

export const STANDARD_ROLES = [
  "seller",
  "branch_manager",
  "network_manager",
  "accountant",
  "administrator",
] as const;

export const FR_ROLES = [
  "fr_seller",
  "fr_branch_manager",
] as const;

export const POS_ROLES = [
  ...STANDARD_ROLES,
  ...FR_ROLES,
] as const;

export const SALE_ROLES = POS_ROLES;

const STANDARD_MANAGER_RETURN_ROLES = [
  "branch_manager",
  "network_manager",
  "accountant",
  "administrator",
] as const;

const OTHER_RETURN_ROLES = [
  ...STANDARD_MANAGER_RETURN_ROLES,
  "fr_branch_manager",
] as const;

export const RETURN_ROLES = [
  "seller",
  ...STANDARD_MANAGER_RETURN_ROLES,
  ...FR_ROLES,
] as const;

export const FINANCIAL_DOCUMENT_ROLES = POS_ROLES;

export const WITHDRAW_WITH_DOCUMENT_ROLES = STANDARD_ROLES;

export const MANGER_ROLES = [
  "branch_manager",
  "network_manager",
  "accountant",
  "administrator",
  "fr_branch_manager",
] as const;

const POLICY_ROLES: Record<AuthorizationPolicy, readonly string[]> = {
  [AuthorizationPolicy.PosAccess]: POS_ROLES,
  [AuthorizationPolicy.Sale]: SALE_ROLES,
  [AuthorizationPolicy.Return]: RETURN_ROLES,
  [AuthorizationPolicy.FinancialDocuments]: FINANCIAL_DOCUMENT_ROLES,
  [AuthorizationPolicy.WithdrawWithDocument]: WITHDRAW_WITH_DOCUMENT_ROLES,
  [AuthorizationPolicy.Cancellation]: MANGER_ROLES,
};

export function isAuthorizedForPolicy(
  userRoles: readonly string[] | null | undefined,
  policy: AuthorizationPolicy | null | undefined,
): boolean {
  if (!policy) {
    return false;
  }

  if (!userRoles?.length) {
    return false;
  }

  const normalizedUserRoles = normalizeUserRoles(userRoles);

  const allowedRoles = POLICY_ROLES[policy];
  return !!allowedRoles?.some(role => normalizedUserRoles.has(role));
}

export function isAuthorizedForReturnPaymentMethod(
  userRoles: readonly string[] | null | undefined,
  method: ReturnPaymentMethod,
): boolean {
  if (method === ReturnPaymentMethod.Benefits) {
    return false;
  }

  const normalizedUserRoles = normalizeUserRoles(userRoles);

  if (method === ReturnPaymentMethod.Voucher) {
    return RETURN_ROLES.some(role => normalizedUserRoles.has(role));
  }

  if (method === ReturnPaymentMethod.Other) {
    return OTHER_RETURN_ROLES.some(role => normalizedUserRoles.has(role));
  }

  return STANDARD_MANAGER_RETURN_ROLES.some(role => normalizedUserRoles.has(role));
}

function normalizeUserRoles(
  userRoles: readonly string[] | null | undefined,
): ReadonlySet<string> {
  return new Set(
    userRoles
      ?.map(role => role?.trim().toLowerCase())
      .filter(Boolean) ?? [],
  );
}
